import axios from "axios";
import { API } from "@/context/AuthContext";

export const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const inr = (v) => {
  const n = r2(v);
  return (n < 0 ? "-" : "") + Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const dmy = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso || "");

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen",
  "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (n) => (n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? `-${ONES[n % 10]}` : ""));
const three = (n) => [Math.floor(n / 100) ? `${ONES[Math.floor(n / 100)]} Hundred` : "", two(n % 100)].filter(Boolean).join(" ");
const indian = (n) => {
  const crore = Math.floor(n / 1e7), lakh = Math.floor((n % 1e7) / 1e5), th = Math.floor((n % 1e5) / 1000), rest = n % 1000;
  return [crore ? `${crore >= 1000 ? indian(crore) : three(crore)} Crore` : "", lakh ? `${two(lakh)} Lakh` : "", th ? `${two(th)} Thousand` : "", rest ? three(rest) : ""]
    .filter(Boolean).join(" ");
};

export const amountInWords = (amount) => {
  const a = Math.abs(r2(amount));
  const rupees = Math.floor(a), paise = Math.round((a - rupees) * 100);
  if (!rupees && !paise) return "Rupees Zero Only.";
  return `${amount < 0 ? "Minus " : ""}Rupees ${indian(rupees)}${paise ? ` and ${two(paise)} Paise` : ""} Only.`;
};

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.style.display = "none";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const fileName = (number, ext) => `PFI-${String(number).replace(/[^A-Za-z0-9_-]+/g, "-")}.${ext}`;
const MIME = { pdf: "application/pdf", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };

export const downloadFile = async (id, number, ext = "pdf") => {
  const res = await axios.get(`${API}/proforma/${id}/${ext}`, { responseType: "blob" });
  saveBlob(new Blob([res.data], { type: MIME[ext] }), fileName(number || id, ext));
};
export const downloadPdf = (id, number) => downloadFile(id, number, "pdf");
export const downloadXlsx = (id, number) => downloadFile(id, number, "xlsx");

export const downloadDraft = async (body, pid, ext = "pdf") => {
  const res = await axios.post(`${API}/proforma/preview`, body, { params: { fmt: ext, ...(pid ? { pid } : {}) }, responseType: "blob" });
  saveBlob(new Blob([res.data], { type: MIME[ext] }), `PFI-DRAFT.${ext}`);
};

// Advance on hand × (selected invoices' open basic ÷ customer's total open basic)
export const apportionAdvance = (customer, invoices, scope, project) => {
  const out = customer?.outstanding || {};
  const adv = (out.advances || []).filter((a) => scope === "all" || (project && (a.project === project || String(a.wbs || "").startsWith(project))));
  const onHand = r2(adv.reduce((s, a) => s + a.amount, 0));
  const selBasic = r2(invoices.reduce((s, i) => s + (i.open_basic || 0), 0));
  const ratio = out.basic_total ? Math.min(1, selBasic / out.basic_total) : 0;
  return { onHand, selBasic, totalBasic: out.basic_total || 0, ratio, amount: r2(onHand * ratio), count: adv.length };
};

// receipts still on account whose SAP text names the project, e.g. "PROJECT NO - 2602"
export const onAccountFor = (customer, project) => (project ? (customer?.outstanding?.on_account || [])
  .filter((p) => new RegExp(`(^|\\D)${project}(\\D|$)`).test(p.text || "")) : []);

export const autoAdjustments = (invoices, adv, retentionPct, exWorks, onAccount = []) => {
  const pays = {};
  onAccount.forEach((p) => { pays[`${p.doc}|${p.date}`] = { date: p.date, amount: p.amount }; });
  invoices.forEach((i) => (i.payments || []).forEach((p) => {
    const k = `${p.doc}|${p.date}`;
    pays[k] = { date: p.date, amount: r2((pays[k]?.amount || 0) + p.amount) };
  }));
  return [
    { kind: "advance", label: "LESS : ADVANCE RECEIVED (apportioned)", amount: adv.amount, sign: -1 },
    { kind: "retention", label: `LESS : RETENTION @ ${retentionPct}%`, amount: r2((exWorks * retentionPct) / 100), sign: -1 },
    ...Object.values(pays).sort((a, b) => a.date.localeCompare(b.date))
      .map((p) => ({ kind: "payment", label: `LESS : RECEIVED PAYMENT DT. ${dmy(p.date)}`, amount: p.amount, sign: -1 })),
  ];
};

export const gstByRate = (lines) => {
  const m = {};
  lines.forEach((l) => { m[l.gst_rate] = r2((m[l.gst_rate] || 0) + (l.tax ?? (l.amount * l.gst_rate) / 100)); });
  return Object.entries(m).filter(([, v]) => v).sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([rate, amount]) => ({ rate: Number(rate), amount, label: `ADD : GST @ ${rate}%` }));
};
